import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Fab,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  FlashOn as FlashOnIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { MensagemRapida } from '../../types/atendimento';
import respostaRapidaService from '../../services/respostaRapidaService';
import { useSnackbar } from 'notistack';

interface FormState {
  atalho: string;
  mensagem: string;
  showAllUsers: boolean;
  ativo: boolean;
}

const formVazio: FormState = { atalho: '', mensagem: '', showAllUsers: false, ativo: true };

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const MensagensRapidasPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [mensagens, setMensagens] = useState<MensagemRapida[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState<MensagemRapida | null>(null);
  const [form, setForm] = useState<FormState>(formVazio);
  const [copiado, setCopiado] = useState<number | null>(null);
  
  const [atalhoError, setAtalhoError] = useState('');

  const fetchMensagens = useCallback(async () => {
    setLoading(true);
    try {
      const response = await respostaRapidaService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: busca || undefined
      });
      if (response.success) {
        setMensagens(response.data || []);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
      enqueueSnackbar('Erro ao buscar mensagens rápidas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, busca, enqueueSnackbar]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMensagens();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchMensagens]);

  const handleNova = () => {
    setEditando(null);
    setForm(formVazio);
    setAtalhoError('');
    setDialogAberto(true);
  };

  const handleEditar = (msg: MensagemRapida) => {
    setEditando(msg);
    setForm({ 
      atalho: msg.atalho, 
      mensagem: msg.mensagem, 
      showAllUsers: msg.showAllUsers, 
      ativo: msg.ativo 
    });
    setAtalhoError('');
    setDialogAberto(true);
  };

  const validarFormulario = () => {
    if (!form.atalho || !form.mensagem) return false;
    const regex = /^[a-zA-Z0-9\/_\-]+$/;
    if (!regex.test(form.atalho)) {
      setAtalhoError('O atalho não pode conter espaços. Use apenas letras, números, /, - ou _.');
      return false;
    }
    setAtalhoError('');
    return true;
  };

  const handleSalvar = async () => {
    if (!validarFormulario()) return;

    try {
      if (editando) {
        await respostaRapidaService.update(editando.id, form);
        enqueueSnackbar('Mensagem atualizada com sucesso!', { variant: 'success' });
      } else {
        await respostaRapidaService.create(form);
        enqueueSnackbar('Mensagem criada com sucesso!', { variant: 'success' });
      }
      setDialogAberto(false);
      fetchMensagens();
    } catch (error: any) {
      console.error('Erro ao salvar mensagem:', error);
      const msg = error?.response?.data?.errors?.[0]?.msg || 'Erro ao salvar mensagem.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleExcluir = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    try {
      await respostaRapidaService.delete(id);
      enqueueSnackbar('Mensagem excluída com sucesso!', { variant: 'success' });
      fetchMensagens();
    } catch (error) {
      console.error('Erro ao excluir mensagem:', error);
      enqueueSnackbar('Erro ao excluir mensagem.', { variant: 'error' });
    }
  };

  const handleToggleAtivo = async (msg: MensagemRapida) => {
    try {
      await respostaRapidaService.update(msg.id, { ativo: !msg.ativo });
      fetchMensagens();
    } catch (error) {
      enqueueSnackbar('Erro ao alterar status.', { variant: 'error' });
    }
  };

  const handleCopiar = (msg: MensagemRapida) => {
    navigator.clipboard.writeText(msg.mensagem).catch(() => {});
    setCopiado(msg.id);
    enqueueSnackbar('Mensagem copiada para a área de transferência', { variant: 'success', autoHideDuration: 2000 });
    setTimeout(() => setCopiado(null), 2000);
  };

  const totalAtivas = mensagens.filter((m) => m.ativo).length;
  const totalCompartilhadas = mensagens.filter((m) => m.showAllUsers).length;

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#fff8e1', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <FlashOnIcon sx={{ fontSize: 120, color: theme.palette.warning.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.35)}`,
              }}
            >
              <FlashOnIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Mensagens Rápidas</Typography>
              <Typography variant="caption" color="text.secondary">Atalhos de texto para agilizar o atendimento</Typography>
            </Box>
          </Stack>
          {!isMobile && (
            <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleNova}>
              Nova Mensagem
            </Button>
          )}
        </Stack>
      </Paper>

      {!loading && totalItems > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Total" valor={totalItems} subtitulo="mensagens cadastradas" icone={<FlashOnIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Ativas" valor={totalAtivas} subtitulo={`${totalItems ? Math.round((totalAtivas / totalItems) * 100) : 0}% do total`} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard titulo="Compartilhadas" valor={totalCompartilhadas} icone={<PublicIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} />
          </Grid>
        </Grid>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <TextField
          size="small"
          placeholder="Buscar mensagem ou atalho..."
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setPage(0); }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Listagem de Mensagens</Typography>
          {!loading && (
            <Chip size="small" label={`${totalItems} mensagem${totalItems !== 1 ? 'ns' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando mensagens...</Typography>
          </Box>
        ) : mensagens.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <FlashOnIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhuma mensagem encontrada</Typography>
            <Typography variant="caption" color="text.secondary">Crie uma nova mensagem ou ajuste a busca.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell>Atalho</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell align="center">Compartilhada</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mensagens.map((msg) => (
                  <TableRow key={msg.id} hover sx={{ opacity: msg.ativo ? 1 : 0.55, '& .MuiTableCell-root': { py: 0.6, px: 1 } }}>
                    <TableCell>
                      <Chip
                        label={msg.atalho}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', height: 20 }}
                      />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                        }}
                      >
                        {msg.mensagem}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={msg.showAllUsers ? 'Sim' : 'Não'}
                        size="small"
                        color={msg.showAllUsers ? 'info' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Switch
                        checked={msg.ativo}
                        onChange={() => handleToggleAtivo(msg)}
                        size="small"
                        color="success"
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.3}>
                        <Tooltip title={copiado === msg.id ? 'Copiado!' : 'Copiar texto'}>
                          <IconButton size="small" color={copiado === msg.id ? 'success' : 'default'} onClick={() => handleCopiar(msg)}>
                            <CopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleEditar(msg)}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => handleExcluir(msg.id)}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              size="small"
              count={totalItems}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Linhas:"
              sx={{
                minHeight: 44,
                '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 },
              }}
            />
          </>
        )}
      </Paper>

      {/* FAB mobile */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 86, right: 16 }}
          onClick={handleNova}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog criar / editar */}
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editando ? 'Editar Mensagem Rápida' : 'Nova Mensagem Rápida'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Atalho"
            size="small"
            fullWidth
            value={form.atalho}
            onChange={(e) => {
              setForm({ ...form, atalho: e.target.value.replace(/ /g, '_') });
              setAtalhoError('');
            }}
            placeholder="Ex: /oi"
            error={!!atalhoError}
            helperText={atalhoError || "Digite o atalho (sem espaços). Ex: /oi, /boas_vindas, /bem-vindo"}
          />
          <TextField
            label="Mensagem"
            size="small"
            fullWidth
            multiline
            rows={5}
            value={form.mensagem}
            onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
            placeholder="Digite o texto da mensagem."
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.showAllUsers}
                onChange={(e) => setForm({ ...form, showAllUsers: e.target.checked })}
                color="info"
              />
            }
            label="Compartilhar com todos os usuários"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                color="success"
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={!form.mensagem.trim() || !form.atalho.trim()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MensagensRapidasPage;
